import { G, Path, Rect } from '@react-pdf/renderer';
import { mapValues } from 'lodash';

/**
* @function Door
* @description Renders a door symbol in PDF floor plans using SVG shapes.
* Supports door positioning, rotation, and scaling.
* 
* @param {Object} props
* @param {string} props.areaId - Area ID containing the door
* @param {number} props.scale - Scale factor for dimensions
* @param {Object} props.content - Room configuration data
* @param {Object} props.content.areas - Areas data by ID
* 
* @typedef {Object} DoorSize
* @property {number} height - Door height in meters (0.9144m)
* @property {number} width - Door width in meters (0.03175m)
* @property {number} arch - Door arch radius (0.88265m)
* 
* @description
* Renders door symbol using:
* 1. Rectangular door frame element
* 2. Curved arch path for swing
* 3. Transforms for position and orientation:
*    - Translation to door location
*    - Rotation for door orientation
*    - Scale for door size adjustment
* 
* @example
* <Door 
*   areaId="room1"
*   scale={0.5}
*   content={{
*     areas: {
*       room1: {
*         door: {
*           x: 10,
*           y: 5,
*           angle: 90,
*           scale: 1
*         }
*       }
*     }
*   }}
* />
* 
* @note
* - Uses @react-pdf/renderer components
* - Dimensions in meters converted to PDF units
* - Door arch drawn with precise geometric rendering
* - Position adjusted by +1 to account for margins
* - All dimensions scaled for PDF output
*/
const DoorSize = {
  height: 0.9144,
  width: 0.03175,
  arch: 0.88265, // height - width
};

const Door = (props) => {
  const { areaId, scale, content: { areas } } = props;
  const { door: { scale: doorScale, angle, x, y } } = areas[areaId];
  const door = mapValues(DoorSize, (v) => v * scale);

  return (
    <G transform={`translate(${(x * scale) + 1}, ${(y * scale) + 1}) rotate(${angle}) scale(${doorScale}) `} >
      <Rect x={0} y={0} width={door.width} height={door.height} stroke='#000' fill='#000' />
      <Path d={`M${door.width}, 0a${door.arch} ${door.arch} 0 0 1 ${door.arch} ${door.height}`} stroke='#000' fill='none' strokeWidth='1' shapeRendering={'geometricprecision'} />
    </G>
  )
};

export default Door